/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import Autocomplete, {
  AutocompleteOwnerState,
  AutocompleteProps
} from '@mui/material/Autocomplete'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import { Theme } from '@mui/material/styles'
import { ClickAwayListener, InputLabel } from '@mui/material'

import { AutoFillType } from 'utils/autofill'
import { InputEventHandlers } from 'utils/tracking/amplitude/WithFieldInteraction'
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  memo,
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  ReactElement
} from 'react'
import { Choice } from 'components/Input/ChoiceGroup'
import { SEPARATOR_VALUE } from 'components/PathSelect'
import useEmotionStyles from 'hooks/useEmotionStyles'
import * as CSSTypes from 'csstype'
import useEmotionStyledComponent from 'hooks/useEmotionStyledComponent'
import { GenericConditionalWrapper } from 'components/GenericConditionalWrapper'

declare global {
  interface Window {
    MSStream: unknown
  }
}
export interface ValueTransformer<T, R = unknown> {
  selector?: keyof T
  extractor?: (raw: T) => R
}

type SelectProps<T, R> = {
  label: string | number
  error?: string
  clue?: string
  placeholder?: string
  value?: string | T
  name?: string
  valueTransformer?: ValueTransformer<T, R>
  optionLabel: keyof T | ((option: T | string) => string)
  onChange?: (value?: R) => void
  path?: string
  noDebounce?: boolean
  outerLabel?: boolean
  smallTextField?: boolean
  dependson?: string
  centeredSelectedValue?: boolean
} & Omit<
  AutocompleteProps<T, false, false, boolean>,
  'renderInput' | 'onChange' | 'defaultValue' | 'value' | 'onBlur' | 'onFocus'
> &
  Omit<InputEventHandlers<T | null>, 'onChange'>

const SELECT_NO_OPTIONS_TEXT_DEFAULT = 'Keine Ergebnisse'

const STREET_PATHS = [
  'debtors.primary.currentAddress.street',
  'debtors.secondary.currentAddress.street',
  'debtors.primary.previousAddress.street',
  'debtors.secondary.previousAddress.street'
]

const styles = (theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as CSSTypes.Property.FlexDirection
  },
  textFieldContainer: {
    display: 'flex',
    justifyContent: 'center',
    background: theme.palette.common.white,

    input: {
      userSelect: 'none' as 'none'
    },

    'input::selection': {
      background: 'transparent'
    }
  },
  labelStyle: {
    fontSize: '14px',
    color: theme.palette.grey[900],
    marginBottom: '8px'
  },
  paper: {
    maxHeight: '290px',
    boxShadow: theme.shadows[3]
  },
  overlay: {
    position: 'absolute' as CSSTypes.Property.Position,
    height: '100%',
    width: '100%',
    zIndex: '1000'
  },
  pointerCursor: {
    cursor: 'pointer'
  },
  containerRefStyle: {
    position: 'relative' as CSSTypes.Property.Position
  }
})

type SelectAutoCompleteProps<T> = Omit<
  AutocompleteProps<T, boolean, boolean, boolean>,
  'getOptionDisabled'
> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionDisabled: (option: any) => boolean
  dependson?: string
}

function StyledAutocomplete<T>(
  props: SelectAutoCompleteProps<T>
): ReactElement {
  const StyledAutocompleteNew = useEmotionStyledComponent<
    (props: AutocompleteProps<T, boolean, boolean, boolean>) => ReactElement
  >(
    () => ({
      root: {
        zIndex: 1,

        '& .MuiTextField-root': {
          margin: 0
        }
      }
    }),
    Autocomplete
  )

  return <StyledAutocompleteNew {...props} />
}

const Select = <T, R>({
  label,
  error,
  clue,
  noOptionsText = SELECT_NO_OPTIONS_TEXT_DEFAULT,
  placeholder,
  options = [],
  onChange,
  onBlur,
  onFocus,
  value,
  name,
  valueTransformer = {},
  optionLabel,
  open = false,
  path,
  outerLabel,
  smallTextField,
  dependson,
  autoHighlight,
  className,
  loading,
  loadingText,
  inputValue,
  renderOption,
  disabled,
  centeredSelectedValue
}: SelectProps<T, R>) => {
  const {
    textFieldContainer,
    container,
    labelStyle,
    overlay,
    containerRefStyle,
    pointerCursor
  } = useEmotionStyles<ReturnType<typeof styles>>(styles)

  const { selector, extractor } = valueTransformer
  const listboxRef = useRef<HTMLDivElement>(null)

  const [isOpen, setOpen] = useState(open)

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  inputRef.current?.setAttribute('readOnly', 'true')

  const inputId =
    label && `text-input-${label.toString().toLowerCase().replace(/\s/g, '')}`

  const isStreetPath = STREET_PATHS.includes(path || '')

  const renderInput = useCallback(
    ({ InputProps, inputProps, ...restTextProps }: TextFieldProps) => {
      const { endAdornment, ...restInputProps } = InputProps!

      return (
        <div className={container}>
          <GenericConditionalWrapper
            condition={Boolean(outerLabel)}
            wrapper={(children: ReactNode) => (
              <InputLabel
                htmlFor={inputId || ''}
                classes={{ root: labelStyle }}
              >
                {label}
                {children}
              </InputLabel>
            )}
          >
            <div className={textFieldContainer}>
              <TextField
                size={smallTextField ? 'small' : undefined}
                label={(!outerLabel && label) || ''}
                id={inputId || ''}
                {...restTextProps}
                inputRef={inputRef}
                variant="outlined"
                color="primary"
                error={!!error}
                style={{ cursor: 'pointer' }}
                helperText={(!isStreetPath && error) || clue}
                FormHelperTextProps={{
                  variant: 'standard',
                  sx: {
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word'
                  }
                }}
                placeholder={placeholder}
                inputProps={{
                  ...inputProps,
                  autoComplete: AutoFillType.OFF
                }}
                name={name}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{
                  ...restInputProps,
                  endAdornment
                }}
                sx={Boolean(outerLabel) ? { marginTop: '8px !important' } : {}}
              />
            </div>
          </GenericConditionalWrapper>
        </div>
      )
    },
    [
      container,
      outerLabel,
      inputId,
      labelStyle,
      label,
      textFieldContainer,
      smallTextField,
      error,
      isStreetPath,
      clue,
      placeholder,
      name
    ]
  )

  const getOptionSelected = useMemo(
    () =>
      selector
        ? (option: T, currentOption: T) =>
            `${option[selector]}` === `${currentOption[selector]}`
        : (option: T, currentOption: T) => option === currentOption,
    [selector]
  )

  const getOptionLabel = useMemo(() => {
    if (typeof optionLabel === 'function') {
      return optionLabel
    }

    return (option: T | string) => {
      if (typeof option === 'string') {
        return ''
      }

      return `${option[optionLabel]}`
    }
  }, [optionLabel])

  const renderOptionCallback = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: T) => {
      const currentOptionLabel = getOptionLabel(option)
      if (currentOptionLabel === '') {
        return null
      }

      const id = selector
        ? `${name || 'option'}_${option[selector]}`
        : undefined

      return (
        /*
          The wrapper that closes the options on click is here, because
          when a value is already selected and a user want to select it once again
          we should close the options manually because the component won't do it
          because there's no value change.
        */
        <div key={id} onClick={() => setOpen(false)}>
          <li {...props}>
            <span id={id}>{currentOptionLabel}</span>
          </li>
        </div>
      )
    },
    [getOptionLabel, selector, name]
  )

  const valueSearchFunc = useMemo(
    () =>
      typeof value === 'string' && selector
        ? (option: T) => `${option[selector]}` === value
        : (option: T) => option === value,
    [selector, value]
  )

  const [selectedValue, setSelectedValue] = useState<T | null>(null)
  const [inputValueState, setInputValueState] = useState('')

  const setValue = useCallback(
    (selected?: T) => {
      if (!selected) {
        return
      }
      setSelectedValue(selected)
      const currentOptionLabel = getOptionLabel(selected)
      const selectedVal = typeof selected === 'string' ? selected : ''
      setInputValueState(currentOptionLabel || selectedVal)

      // This is used to automatically hide the keyboard after the user selected something
      inputRef.current?.blur()
    },
    [getOptionLabel]
  )

  useEffect(() => {
    setValue(options?.find(valueSearchFunc))
  }, [options, setValue, value, valueSearchFunc])

  useEffect(() => {
    if (isOpen && centeredSelectedValue) {
      setTimeout(() => {
        if (listboxRef.current) {
          const selected = listboxRef.current.querySelector(
            '[aria-selected="true"]'
          )
          selected?.scrollIntoView({ block: 'center' })
        }
      }, 50) // Small delay to ensure rendering
    }
  }, [isOpen])

  const changeHandler = useCallback(
    (_: ChangeEvent<unknown>, choice?: T) => {
      setOpen(false)
      if (choice) {
        setValue(choice)
      }

      if (!onChange) {
        return
      }

      const newValue =
        choice && extractor
          ? extractor(choice)
          : (choice as unknown as R | undefined)

      onChange(newValue)
    },
    [onChange, extractor, setValue]
  )

  const blurHandler = useCallback(() => {
    setOpen(false)
    if (onBlur) {
      onBlur(selectedValue)
    }
  }, [onBlur, selectedValue])

  const focusHandler = useCallback(() => {
    if (onFocus) {
      onFocus(selectedValue)
    }
  }, [onFocus, selectedValue])

  const openAutoComplete = () => {
    if (isOpen) {
      setOpen(false)
    }
    if (
      containerRef?.current &&
      containerRef?.current?.getElementsByTagName('button') &&
      !isOpen &&
      !disabled
    ) {
      containerRef?.current?.getElementsByTagName('button')[0].click()
      setOpen(true)
    }
  }

  const keydownHandler = useCallback(
    (keyboardEvent: KeyboardEvent<HTMLElement>) => {
      if (
        !disabled &&
        (keyboardEvent.key === 'ArrowUp' ||
          keyboardEvent.key === 'ArrowDown' ||
          keyboardEvent.key === 'Enter')
      ) {
        setOpen(true)
      }
    },
    [setOpen, disabled]
  )

  // empty value '' is being used a default value, it needs to be one of the option in `options`
  const emptyValue = '' as T
  const optionsWithEmptyValue = [...options, emptyValue]

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div
        data-testid="simple-select"
        ref={containerRef}
        className={containerRefStyle}
      >
        <div
          data-testid="simple-select-trigger"
          className={`${overlay} ${!disabled ? pointerCursor : ''}`}
          onClick={openAutoComplete}
        />
        <StyledAutocomplete<T>
          open={isOpen}
          value={selectedValue || emptyValue}
          inputValue={inputValue || inputValueState}
          ListboxProps={{
            ref: listboxRef,
            sx: {
              maxHeight: '300px',
              overflowY: 'auto'
            }
          }}
          renderOption={(props, option, state, ownerState) =>
            (renderOption || renderOptionCallback)(
              props,
              option,
              state,
              ownerState as AutocompleteOwnerState<
                T,
                false,
                false,
                boolean,
                'div'
              >
            )
          }
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={getOptionSelected}
          options={optionsWithEmptyValue}
          fullWidth
          disableClearable
          renderInput={renderInput}
          noOptionsText={noOptionsText}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onChange={changeHandler as any} // Material UI types are not cool here :/
          onBlur={blurHandler}
          onFocus={focusHandler}
          id={name}
          getOptionDisabled={(option: Choice<T>) =>
            (option?.value as unknown as string) === SEPARATOR_VALUE
          }
          dependson={dependson}
          defaultValue=""
          autoHighlight={autoHighlight}
          className={className}
          loading={loading}
          loadingText={loadingText}
          onKeyDown={keydownHandler}
          disabled={disabled}
        />
      </div>
    </ClickAwayListener>
  )
}

export default memo(
  Select,
  (prevProps, nextProps) =>
    JSON.stringify(prevProps) === JSON.stringify(nextProps)
) as typeof Select
